import { Button } from "@frontend/components/ui/button"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { type CreatedApiKey, useCreateApiKey } from "@frontend/pages/settings/hooks/use-api-keys"
import { zodResolver } from "@hookform/resolvers/zod"
import { Check, Copy, Key, X } from "lucide-react"
import type { FormEvent } from "react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

const createKeySchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  expires_at: z.string().optional()
})

type CreateKeyFormData = z.infer<typeof createKeySchema>

export interface CreateApiKeyDialogViewProps {
  open: boolean
  createdKey: CreatedApiKey | null
  copied: boolean
  nameError?: string
  apiError?: string
  isPending: boolean
  minDate: string
  onClose: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  register: ReturnType<typeof useForm<CreateKeyFormData>>["register"]
  onCopy: () => void
}

export function CreateApiKeyDialogView({
  open,
  createdKey,
  copied,
  nameError,
  apiError,
  isPending,
  minDate,
  onClose,
  onSubmit,
  register,
  onCopy
}: CreateApiKeyDialogViewProps) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-50 w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            <h2 className="text-lg font-semibold">{createdKey ? "API Key Created" : "Create API Key"}</h2>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {createdKey ? (
          <div className="space-y-4">
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                Make sure to copy your API key now. You won't be able to see it again.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Your API Key</Label>
              <div className="flex gap-2">
                <Input value={createdKey.key} readOnly className="font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={onCopy} title="Copy to clipboard">
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <p>
                <strong>Name:</strong> {createdKey.name}
              </p>
              {createdKey.expires_at ? (
                <p>
                  <strong>Expires:</strong> {new Date(createdKey.expires_at).toLocaleDateString()}
                </p>
              ) : null}
            </div>

            <div className="flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Key Name</Label>
              <Input
                id="key-name"
                placeholder="e.g., SDK Key, CI/CD Pipeline"
                {...register("name")}
                aria-invalid={Boolean(nameError)}
              />
              {nameError ? <p className="text-xs text-destructive">{nameError}</p> : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="key-expires">
                Expiration Date <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input id="key-expires" type="date" {...register("expires_at")} min={minDate} />
              <p className="text-xs text-muted-foreground">Leave empty for no expiration.</p>
            </div>

            {apiError ? <p className="text-xs text-destructive">{apiError}</p> : null}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Creating..." : "Create Key"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

interface CreateApiKeyDialogContainerProps {
  open: boolean
  onClose: () => void
}

export function CreateApiKeyDialogContainer({ open, onClose }: CreateApiKeyDialogContainerProps) {
  const createKey = useCreateApiKey()
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null)
  const [copied, setCopied] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateKeyFormData>({
    resolver: zodResolver(createKeySchema),
    defaultValues: {
      name: "",
      expires_at: ""
    }
  })

  const onSubmit = (data: CreateKeyFormData) => {
    const payload: { name: string; expires_at?: string | null } = {
      name: data.name
    }

    if (data.expires_at) {
      payload.expires_at = new Date(data.expires_at).toISOString()
    }

    createKey.mutate(payload, {
      onSuccess: (result) => {
        setCreatedKey(result)
      }
    })
  }

  const handleCopy = async () => {
    if (createdKey?.key) {
      await navigator.clipboard.writeText(createdKey.key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleClose = () => {
    setCreatedKey(null)
    setCopied(false)
    reset()
    createKey.reset()
    onClose()
  }

  return (
    <CreateApiKeyDialogView
      open={open}
      createdKey={createdKey}
      copied={copied}
      nameError={errors.name?.message}
      apiError={createKey.isError ? createKey.error.message : undefined}
      isPending={createKey.isPending}
      minDate={new Date().toISOString().split("T")[0] ?? ""}
      onClose={handleClose}
      onSubmit={handleSubmit(onSubmit)}
      register={register}
      onCopy={handleCopy}
    />
  )
}

export const CreateApiKeyDialog = CreateApiKeyDialogContainer
